import { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Lookup } from "@/models/Lookup";
import { auditRunReport } from "@/services/auditService";
import { getPersonAccessReport } from "@/services/reportService";
import { getErrorMessage, showError } from "@/services/utilities/ServiceError";
import { ListQuery } from "@/models/ListQuery";
import PersonAccessReportColumns from "@/models/ColumnDefinitions/PersonAccessReportColumns";
import PersonAccessReportSearch from "@/models/search/PersonAccessReportSearch";
import { DEF_PG_SIZE_REPORTS } from "@/constants/maximum-page-size";
import { Filter } from "@/models/Filter";
import { toast } from "react-toastify";
import { REPORTS_PERSON_ACCESS } from "@/constants/menu-types";
import { getUserFiltersByView } from "@/services/userPreferenceService";
import {FILTER_ACCESS_GROUP, FILTER_AREA, FILTER_BUILDINGS, FILTER_DOORS, FILTER_FLOORS, FILTER_SITES, FILTER_TAGHOLDERS} from "@/constants/filter-types";
import LookupTypes from "@/models/LookupTypes";
import { getEnrolmentNamesWithPagination } from "@/services/enrolmentService";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";
import { getSiteNamesWithPagination } from "@/services/siteService";
import SiteSearch from "@/models/search/SiteSearch";
import { getAccessAreaNamesWithPagination } from "@/services/accessAreaService";
import AccessAreaSearch from "@/models/search/AccessAreaSearch";
import { getDoorNamesWithPagination } from "@/services/doorService";
import DoorSearch from "@/models/search/DoorSearch";
import { getBuildingNamesWithPagination } from "@/services/buildingService";
import BuildingSearch from "@/models/search/BuildingSearch";
import {getAccessGroupsNamesWithPagination} from "@/services/accessGroupService.ts";
import AccessGroupSearch from "@/models/search/AccessGroupSearch.ts";
import FloorSearch from "@/models/search/FloorSearch.ts";
import {getFloorsNamesWithPagination} from "@/services/floorService.ts";

interface PersonAccessReportProps {
    reportId: string;
}

const PersonAccessReport: FC<PersonAccessReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const lookup: Lookup[] = [
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "locationId",
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "areaId",
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "accessGroupId",
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessGroupsNamesWithPagination(null, listQuery),
            searchAccessor: AccessGroupSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect:true
        },
        {
            name: "floorId",
            values: [],
            dataMethod: (listQuery?: ListQuery) => getFloorsNamesWithPagination(listQuery),
            searchAccessor: FloorSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect:true
        },
        {
            name: "buildingId",
            values: [],
            dataMethod: getBuildingNamesWithPagination,
            searchAccessor: BuildingSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        }
    ];

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "masterId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.required),
                        value: "require_filter",
                        ignore: true
                    }],
                };
            case "locationId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.door),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                };
            case "areaId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.accessArea),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                };
            case "siteId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                };
            case "buildingId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.building),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                };
            default:
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                };
        }
    };

    const initialiseUserPreferences = async (setDetails: any) => {
        const staticFilterKeys: string[] = ["masterId", "siteId", "areaId", "locationId", "buildingId"];
        let customFilter: Filter[] = [];

        try {
            const response = await getUserFiltersByView(userLoginId, REPORTS_PERSON_ACCESS, reportId);

            if (response.find(filter => filter.filterId === FILTER_TAGHOLDERS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("masterId"));
            }
            if (response.find(filter => filter.filterId === FILTER_DOORS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("locationId"));
            }
            if (response.find(filter => filter.filterId === FILTER_AREA) === undefined) {
                customFilter.push(await getOriginalStaticFilter("areaId"));
            }
            if (response.find(filter => filter.filterId === FILTER_SITES) === undefined) {
                customFilter.push(await getOriginalStaticFilter("siteId"));
            }
            if (response.find(filter => filter.filterId === FILTER_BUILDINGS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("buildingId"));
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setDetails(userLoginId, REPORTS_PERSON_ACCESS, customFilter, reportId, staticFilterKeys);
        }
    };

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getPersonAccessReport(translate, toast.warn, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    };

    return <TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={PersonAccessReportColumns}
        searchDescriptor={PersonAccessReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        getOriginalStaticFilter={getOriginalStaticFilter}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.personAccessReport)}
            selectRows={false}
            cyName="PersonAccessReport"/>
    </TableContextProvider>;
};

export default PersonAccessReport;
